import { zipFormats } from "@/formats"
import { computed, defineComponent } from "vue"
import { useI18n } from "vue-i18n"

export type ZipValidationResult = {
    /**
     * When no zip or country is given, the result is dirty and does not need to be shown to the user.
     */
    dirty: boolean

    /**
     * When the zip code is valid, this is true. If the zip code is invalid, this is false.
     */
    valid: boolean

    /**
     * The formats that are valid for the selected country. This is used to show the user the correct format
     * for the zip code.
     */
    formats: string[]
}

export function validateZip(zipCode: string | undefined, country: string | undefined): ZipValidationResult {
    if (!country || !zipCode || !(country in zipFormats)) {
        return {
            dirty: true,
            valid: false,
            formats: [],
        }
    }

    const formats = zipFormats[country]

    for (const format of formats) {
        const regex = new RegExp(translateFormatToRegex(format))
        console.log(`Validating ${zipCode} against ${format} (${regex})`, regex.test(zipCode))
        if (regex.test(zipCode)) {
            return {
                dirty: false,
                valid: true,
                formats: formats,
            }
        }
    }

    return {
        dirty: false,
        valid: false,
        formats,
    }
}

function translateFormatToRegex(format: string) {
    format = format.replace(/#/g, "\\d")
    format = format.replace(/@/g, "[a-zA-Z]")
    format = format.replace(/\*/g, "[a-zA-Z0-9]")
    format = format.replace(/ /g, " ?")

    return `^${format}$`
}

export const ZipValidationMessage = defineComponent<{
    zip?: string
    country?: string
    result?: ZipValidationResult
}>({
    name: "ZipValidationMessage",
    props: ["zip", "country", "result"],
    setup(props) {
        const result = computed(() => props.result ?? validateZip(props.zip, props.country))
        const { t } = useI18n()

        return () => {
            if (result.value.valid || result.value.dirty) {
                return null
            }

            return (
                <div class="text-danger">
                    {t("@app:zip_validation.invalid_zip", { formats: result.value.formats.join(", ") })}
                </div>
            )
        }
    },
})
